import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { forms, textinputs } from 'seal-ui-components';
import ExpandablePanel from '@Components/UI/ExpandablePanel/ExpandablePanel';
import DropDownIconButton from '@Components/UI/DropDownIconButton/DropDownIconButton';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import { SortOrder, SearchType, contractSearch, clearSearch } from '@Actions/contractsActions';
import { NOTIFICATION_HANDLER, CONTRACT_SEARCH, MIX_PANEL } from '@Constants/types';
import store from '@Store';
import ContractsList from './components/ContractsList/ContractsList';
import {
    CheckBoxWrapper,
    InBodyTitle,
    SearchBodyContainer,
    ContractsContainerWrapper,
    CheckBoxContainer
} from './ContractsContainer.style';
import mixpanelEvents from '@Mixpanel';

const ContractsContainer = () => {
    const [panelChecked, setPanelChecked] = useState(true);
    const [checkBoxChecked, setCheckBoxChecked] = useState(true);
    const [selectedItem, setSelectedItem] = useState('basic');
    const [searchQuery, setSearchQuery] = useState('');
    const [userToggledCheckbox, setUserToggledCheckbox] = useState(false);

    const dispatch = useDispatch();

    const {
        moreAvailable,
        searchResultsPageSize,
        searchingInProgress,
        totalSearchResults,
        lastSearchQuery,
        lastUploadedByMe,
        lastHistory,
        lastSearchType
    } = useSelector(state => state.contract);

    const SystemPanelButtonClick = () => {
        setPanelChecked(!panelChecked);
    };

    const searchHandler = (value, clear, offset) => {
        try {
            if (clear) dispatch(clearSearch());

            store.dispatch({
                type: CONTRACT_SEARCH.SEARCH_CRITERIA,
                payload: {
                    lastSearchQuery: value,
                    lastUploadedByMe: checkBoxChecked,
                    lastSearchType: selectedItem
                }
            });

            switch (selectedItem) {
                case 'advanced':
                    mixpanelEvents.searchcontractsolr();
                    dispatch(
                        contractSearch(
                            value,
                            offset,
                            searchResultsPageSize,
                            checkBoxChecked,
                            SortOrder.Ascending,
                            'Filename',
                            SearchType.Advanced
                        )
                    );
                    break;
                default:
                    const mixpanelContracts = {
                        uploadedByMe: checkBoxChecked
                    };

                    store.dispatch({
                        type: MIX_PANEL.MIXPANEL_DATA_CONTRACTS,
                        payload: mixpanelContracts
                    });

                    mixpanelEvents.searchcontractname();
                    dispatch(
                        contractSearch(
                            value,
                            offset,
                            searchResultsPageSize,
                            checkBoxChecked,
                            SortOrder.Ascending,
                            'Filename',
                            SearchType.Basic
                        )
                    );
                    break;
            }
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'ContractsContainer.searchHandler', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
        }
    };

    const onKeyUp = event => {
        if (event.which === 13 && !searchingInProgress) searchHandler(event.target.value, true, 0);
    };

    const onChange = newValue => setSearchQuery(newValue);

    useEffect(() => {
        if (!totalSearchResults && !lastHistory) {
            searchHandler(searchQuery, true, 0);
        }
    }, []);

    useEffect(
        () => {
            if (userToggledCheckbox) {
                setUserToggledCheckbox(false);
                searchHandler(searchQuery, true, 0);
            } else {
                // switching back to this tab
                setSearchQuery(lastSearchQuery);
                setCheckBoxChecked(lastUploadedByMe);
                if (lastSearchType) setSelectedItem(lastSearchType);
            }
        },
        [checkBoxChecked]
    );

    const UploadedByMeCheckChange = () => {
        if (!searchingInProgress) {
            setUserToggledCheckbox(true);
            dispatch(clearSearch());

            const mixpanelContracts = {
                uploadedByMe: !checkBoxChecked,
                fileType: ''
            };

            store.dispatch({
                type: MIX_PANEL.MIXPANEL_DATA_CONTRACTS,
                payload: mixpanelContracts
            });

            setCheckBoxChecked(!checkBoxChecked);
        }
    };

    const ShowMore = () => {
        if (moreAvailable) {
            store.dispatch({
                type: CONTRACT_SEARCH.SEARCH_NEXT_PAGE
            });

            // Fetch the currentSearchPageOffset immediately, as the component state is
            // not updated in time.
            // Note: We cannot use a useEffect[currentSearchPageOffset] here because the tab switching
            //       is causing this event to fire and therefore triggering an unwanted search
            const rootReducer = store.getState();
            const { contract } = rootReducer;
            const { currentSearchPageOffset: offset } = contract;

            searchHandler(searchQuery, false, offset);
        }
    };

    const onClearSearch = () => {
        searchHandler('', true, 0);
    };

    return (
        <ContractsContainerWrapper>
            <ExpandablePanel
                title="Search and filter"
                controlledPanelCheck={panelChecked}
                onToggleClick={() => SystemPanelButtonClick()}
                testid="contracts_filter_menu_arrow_button"
            >
                <InBodyTitle>Search contracts</InBodyTitle>
                <SearchBodyContainer>
                    <textinputs.Search
                        id="searchTextbox"
                        placeholder="Search contracts"
                        onKeyUp={onKeyUp}
                        onChange={e => onChange(e.currentTarget.value)}
                        onClear={() => onClearSearch()}
                        value={searchQuery}
                        data-testid="contracts_search_field"
                    />
                    <DropDownIconButton
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                    />
                </SearchBodyContainer>
                {selectedItem === 'basic' && (
                    <CheckBoxContainer>
                        <CheckBoxWrapper>
                            <forms.CheckboxField
                                data-testid="upload-byMe-check"
                                label="Uploaded by me"
                                defaultChecked={checkBoxChecked}
                                onChange={() => UploadedByMeCheckChange()}
                            />
                        </CheckBoxWrapper>
                    </CheckBoxContainer>
                )}
            </ExpandablePanel>

            <ContractsList data-testid="contracts-list-container" showMore={ShowMore} />
        </ContractsContainerWrapper>
    );
};

export default ContractsContainer;
